import { useMediaQuery } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHoverMenu } from '@/hooks/useHoverMenu';
import usePermission from '@/hooks/usePermission';
import { useTourContext } from '@/store/tourContext';
import { navigationItems } from '@/utils/navigationItems';
import { tutorialActions } from '@/utils/tutorials';
import { useTutorialActive } from './useTutorialActive';

/**
 * Props for useTopbarTutorial hook
 */
interface UseTopbarTutorialProps {
	/** Ref for the cart button in the topbar */
	cartButtonRef: React.RefObject<HTMLButtonElement | null>;
	/** Ref for the Benefits Verification navigation item */
	bvNavItemRef: React.RefObject<HTMLButtonElement | null>;
	/** Ref for the Orders navigation item (parent menu) */
	ordersNavItemRef: React.RefObject<HTMLElement | null>;
	/** Ref for the Accounting & Finance navigation item (parent menu) */
	accountingFinanceNavItemRef: React.RefObject<HTMLElement | null>;
	/** Ref for the Invoices navigation item (parent menu) */
	invoicesNavItemRef: React.RefObject<HTMLElement | null>;
	/** Callback to set the open menu state */
	setOpenMenu: (menu: string | null, forceClose?: boolean) => void;
	/** Callback to receive refs from submenu items */
	onSubmenuItemRef?: (itemTitle: string, element: HTMLElement | null) => void;
	/** Callback to open mobile drawer */
	openDrawer?: () => void;
	/** Callback to set expanded items in mobile drawer */
	setDrawerExpandedItems?: (items: string[]) => void;
	/** Expanded items for mobile drawer */
	drawerExpandedItems?: string[];
}

/**
 * Custom hook to manage all Topbar tutorial logic
 * Handles tutorial detection, element registration, and menu opening/closing
 *
 * This hook centralizes the logic for highlighting topbar elements during tutorials
 *
 * Key behavior:
 * - Registers navigation elements when available
 * - Menus only open when explicitly called via the 'openTobBarMenu' action
 * - Menus close when 'closeTopBarMenu' action is called (typically via onNextAction)
 * - Normal hover behavior when no tutorial is active
 *
 */
export const useTopbarTutorial = ({
	bvNavItemRef,
	ordersNavItemRef,
	accountingFinanceNavItemRef,
	invoicesNavItemRef,
	setOpenMenu,
	onSubmenuItemRef,
	openDrawer,
	setDrawerExpandedItems,
	drawerExpandedItems = [],
}: UseTopbarTutorialProps) => {
	const { registerElement, isTourActive, activeTutorialName } =
		useTourContext();
	const { cancelClose } = useHoverMenu(250);
	const { hasPermission } = usePermission();
	const [isSidebarMode] = useMediaQuery(['(max-width: 1030px)']);
	// Flags to track if menus were explicitly closed (via closeTopBarMenu action)
	const invoicesMenuExplicitlyClosedRef = useRef(false);
	const ordersMenuExplicitlyClosedRef = useRef(false);
	const [tourExpandSubmenu, setTourExpandSubmenu] = useState<string | null>(
		null,
	);
	const {
		isInvoicesTutorialActive,
		isInvoicesSalesRepTutorialActive,
		isReturnsTutorialActive,
		isRequestsTutorialActive,
		isOnboardingActive,
	} = useTutorialActive();


	// Check if user has permission to view Accounting & Finance menu
	const hasAccountingFinancePermission = useMemo(() => {
		const accountingFinanceItem = navigationItems.find(
			(item) => item.title === 'Accounting & Finance',
		);
		return (
			accountingFinanceItem &&
			(!accountingFinanceItem.permissions ||
				accountingFinanceItem.permissions.length === 0 ||
				hasPermission(accountingFinanceItem.permissions))
		);
	}, [hasPermission]);

	// Refs for submenu items - these are always tracked
	const openInvoicesNavItemRef = useRef<HTMLElement | null>(null);
	const [openInvoicesElement, setOpenInvoicesElement] =
		useState<HTMLElement | null>(null);
	const openInvoicesSalesRepNavItemRef = useRef<HTMLElement | null>(null);
	const [openInvoicesSalesRepElement, setOpenInvoicesSalesRepElement] =
		useState<HTMLElement | null>(null);
	const returnsNavItemRef = useRef<HTMLElement | null>(null);
	const [returnsElement, setReturnsElement] = useState<HTMLElement | null>(
		null,
	);
	const orderStatusNavItemRef = useRef<HTMLElement | null>(null);
	const [orderStatusElement, setOrderStatusElement] =
		useState<HTMLElement | null>(null);
	const [ordersElement, setOrdersElement] = useState<HTMLElement | null>(null);
	const [accountingFinanceElement, setAccountingFinanceElement] =
		useState<HTMLElement | null>(null);
	const [invoicesElement, setInvoicesElement] = useState<HTMLElement | null>(
		null,
	);

	useEffect(() => {
		const element = ordersElement || ordersNavItemRef.current;
		registerElement('ordersNavItemRef', element);
	}, [ordersElement, ordersNavItemRef, registerElement]);

	useEffect(() => {
		// Only register Accounting & Finance element if user has permission
		if (!hasAccountingFinancePermission) {
			registerElement('accountingFinanceNavItemRef', null);
			return;
		}
		const element =
			accountingFinanceElement || accountingFinanceNavItemRef.current;
		registerElement('accountingFinanceNavItemRef', element);
	}, [
		accountingFinanceElement,
		accountingFinanceNavItemRef,
		registerElement,
		hasAccountingFinancePermission,
	]);

	useEffect(() => {
		const element = invoicesElement || invoicesNavItemRef.current;
		registerElement('invoicesNavItemRef', element);
	}, [invoicesElement, invoicesNavItemRef, registerElement]);

	useEffect(() => {
		const element = openInvoicesElement || openInvoicesNavItemRef.current;
		registerElement('InvoicesNavItemRef', element);
	}, [openInvoicesElement, registerElement]);

	useEffect(() => {
		const element =
			openInvoicesSalesRepElement || openInvoicesSalesRepNavItemRef.current;
		registerElement('InvoicesSalesRepNavItemRef', element);
	}, [openInvoicesSalesRepElement, registerElement]);

	useEffect(() => {
		const element = returnsElement || returnsNavItemRef.current;
		registerElement('ReturnsNavItemRef', element);
	}, [returnsElement, registerElement]);

	useEffect(() => {
		const element = orderStatusElement || orderStatusNavItemRef.current;
		registerElement('OrdersNavItemRef', element);
	}, [orderStatusElement, registerElement]);

	const handleSubmenuItemRef = (
		itemTitle: string,
		element: HTMLElement | null,
	) => {
		if (itemTitle === 'Open Invoices' && element) {
			// Always update both refs - they point to the same element
			// The tour context will use the correct one based on the active tutorial
			openInvoicesNavItemRef.current = element;
			setOpenInvoicesElement(element);
			openInvoicesSalesRepNavItemRef.current = element;
			setOpenInvoicesSalesRepElement(element);
		}
		if (itemTitle === 'Return Orders' && element) {
			returnsNavItemRef.current = element;
			setReturnsElement(element);
		}
		if (itemTitle === 'Order Status' && element) {
			orderStatusNavItemRef.current = element;
			setOrderStatusElement(element);
		}

		onSubmenuItemRef?.(itemTitle, element);
	};

	// Check if any invoice-related tutorial is active (all invoice and onboarding tutorials)
	const {
		isAnyInvoiceOrOnboardingTutorialActive: isAnyInvoiceTutorialActive,
	} = useTutorialActive();

	// Check if any orders-related tutorial is active (includes Returns and Requests, plus onboarding that uses returns)
	const isAnyOrdersTutorialActive = useMemo(() => {
		return (
			isReturnsTutorialActive ||
			isRequestsTutorialActive ||
			(isOnboardingActive &&
				(activeTutorialName === 'OnboardingWCTutorial' ||
					activeTutorialName === 'OnboardingSalesRepTutorial' ||
					activeTutorialName === 'OnboardingMindsightTutorial'))
		);
	}, [
		isReturnsTutorialActive,
		isRequestsTutorialActive,
		isOnboardingActive,
		activeTutorialName,
	]);

	// Reset flags and submenu expansion when tutorial ends
	useEffect(() => {
		if (!isTourActive) {
			invoicesMenuExplicitlyClosedRef.current = false;
			ordersMenuExplicitlyClosedRef.current = false;
			setTourExpandSubmenu(null);
		}
	}, [isTourActive]);

	const setOrdersNavItemRef = (element: HTMLElement | null) => {
		if (element) {
			setOrdersElement(element);
		}
	};

	const setAccountingFinanceNavItemRef = (element: HTMLElement | null) => {
		if (element) {
			setAccountingFinanceElement(element);
		}
	};

	const setInvoicesNavItemRef = (element: HTMLElement | null) => {
		if (element) {
			setInvoicesElement(element);
		}
	};

	useEffect(() => {
		tutorialActions.register('closeTopBarMenu', () => {
			if (!isTourActive) return; // Only act when tour is active (e.g. from onNextAction)
			cancelClose();
			setTourExpandSubmenu(null);

			invoicesMenuExplicitlyClosedRef.current = true;
			ordersMenuExplicitlyClosedRef.current = true;

			// In mobile/tablet mode, close drawer instead of closing hover menus
			if (isSidebarMode) {
				// Dispatch event to close drawer
				try {
					window.dispatchEvent(new CustomEvent('tour:closeDrawer'));
				} catch (error) {
					console.error('[Tour] Error dispatching close drawer event:', error);
				}
				// Also collapse items if we have the setter
				if (setDrawerExpandedItems) {
					setDrawerExpandedItems([]);
				}
				return;
			}

			const closeSubmenus = () => {
				const allElements = document.querySelectorAll('div');
				allElements.forEach((element) => {
					const el = element as HTMLElement;
					const style = window.getComputedStyle(el);
					const rect = el.getBoundingClientRect();

					const zIndex = parseInt(style.zIndex) || 0;
					if (
						style.position === 'fixed' &&
						style.display !== 'none' &&
						style.visibility !== 'hidden' &&
						rect.width > 0 &&
						rect.height > 0 &&
						zIndex >= 9999 &&
						(style.borderRadius !== '0px' || style.boxShadow !== 'none')
					) {
						const mouseLeaveEvent = new MouseEvent('mouseleave', {
							bubbles: true,
							cancelable: true,
							view: window,
						});

						el.dispatchEvent(mouseLeaveEvent);
						const children = el.querySelectorAll('*');
						children.forEach((child) => {
							(child as HTMLElement).dispatchEvent(mouseLeaveEvent);
						});

						el.style.display = 'none';
						el.style.visibility = 'hidden';
						el.style.opacity = '0';
					}
				});
			};

			closeSubmenus();

			setOpenMenu(null, true);

			setTimeout(() => {
				closeSubmenus();
			}, 50);

			setTimeout(() => {
				closeSubmenus();
			}, 200);
		});

		const openMenuByType = (type: 'invoices' | 'orders' | 'salesInvoices') => {
			if (type === 'invoices' || type === 'salesInvoices') {
				invoicesMenuExplicitlyClosedRef.current = false;
			} else {
				ordersMenuExplicitlyClosedRef.current = false;
			}

			if (isSidebarMode && openDrawer && setDrawerExpandedItems) {
				openDrawer();
				setTimeout(() => {
					const itemsToExpand: string[] = [];
					if (type === 'invoices') {
						if (hasAccountingFinancePermission) {
							itemsToExpand.push('Accounting & Finance');
							itemsToExpand.push('Accounting & Finance-Invoices');
						} else {
							itemsToExpand.push('Invoices');
						}
					} else if (type === 'salesInvoices') {
						// Sales Rep: always standalone Invoices (contains Open Invoices)
						itemsToExpand.push('Invoices');
					} else {
						itemsToExpand.push('Orders');
					}
					if (itemsToExpand.length > 0) {
						setDrawerExpandedItems(itemsToExpand);
					}
				}, 300);
				return;
			}

			// Desktop: open hover menu
			if (type === 'invoices') {
				if (hasAccountingFinancePermission) {
					setTourExpandSubmenu('Invoices');
					const accountingFinanceItem = navigationItems.find(
						(item) => item.title === 'Accounting & Finance',
					);
					if (accountingFinanceItem) {
						setOpenMenu('Accounting & Finance');
						cancelClose();
					}
				} else {
					setTourExpandSubmenu(null);
					const invoicesItem = navigationItems.find(
						(item) => item.title === 'Invoices',
					);
					if (invoicesItem) {
						setOpenMenu('Invoices');
						cancelClose();
					}
				}
			} else if (type === 'salesInvoices') {
				// Sales Rep: always standalone Invoices menu (contains Open Invoices)
				setTourExpandSubmenu(null);
				const invoicesItem = navigationItems.find(
					(item) => item.title === 'Invoices',
				);
				if (invoicesItem) {
					setOpenMenu('Invoices');
					cancelClose();
				}
			} else {
				setTourExpandSubmenu(null);
				const ordersItem = navigationItems.find(
					(item) => item.title === 'Orders',
				);
				if (ordersItem) {
					setOpenMenu('Orders');
					cancelClose();
				}
			}
		};

		// onPrevAction: open Invoices menu (topbar or lateral drawer on small screens)
		tutorialActions.register('openInvoicesTopBarMenu', () => {
			openMenuByType('invoices');
		});
		// Alias for typo in backend tutorial JSON (openInvoiceTobBarMenu)
		tutorialActions.register('openInvoiceTobBarMenu', () => {
			openMenuByType('invoices');
		});

		// onPrevAction: open Orders menu (topbar or lateral drawer on small screens)
		tutorialActions.register('openOrderTopBarMenu', () => {
			openMenuByType('orders');
		});

		// onPrevAction: open Returns menu → same as Orders (Returns is under Orders)
		tutorialActions.register('openReturnTopBarMenu', () => {
			openMenuByType('orders');
		});

		// onPrevAction: open Sales Rep Invoices menu (standalone Invoices with Open Invoices)
		tutorialActions.register('openSalesInvoicesTopBarMenu', () => {
			openMenuByType('salesInvoices');
		});

		tutorialActions.register('openTobBarMenu', (step) => {
			// Determine which menu to open based on step reference if provided
			// This allows the action to know exactly which menu to open during onboarding
			const stepReferenceRaw = step?.reference;
			// Handle both string and string[] types for reference
			const stepReference = Array.isArray(stepReferenceRaw)
				? stepReferenceRaw[0]
				: (stepReferenceRaw as string | undefined);
			
			const invoicesSubmenuRefs = [
				'InvoicesNavItemRef',
				'InvoicesSalesRepNavItemRef',
			];
			// Refs for elements on the Invoices page (columns, cart, etc.) - when going back, open Invoices menu
			const invoicesPageRefs = [
				'InvoicesColumnInvoiceNumberRef',
				'InvoicesColumnProductRef',
				'InvoicesColumnTIDRef',
				'InvoicesColumnBalanceRef',
				'InvoicesColumnPatientNameRef',
				'InvoicesColumnStatusRef',
				'InvoicesCheckboxColumnRef',
				'InvoicesCheckboxColumnRef2',
				'InvoicesAddToCartAction',
				'InvoicesShoppingCartIconRef',
				'InvoicesCartTrashIconRef',
				'InvoicesCartCheckoutButtonRef',
				'InvoicesStripeDisclaimerRef',
				'InvoicesConfirmButtonRef',
				'InvoicesActionViewRef',
				'InvoicesViewModalRef',
			];
			const ordersSubmenuRefs = [
				'ReturnsNavItemRef',
				'OrdersNavItemRef',
			];
			// Refs for elements on Returns/Orders/Requests pages - when going back, open Orders menu
			const ordersPageRefs = [
				'ReturnsAddButtonRef',
				'ReturnsPatientFormRef',
				'ReturnsQuestionnaireRef',
				'ReturnsTissueIdFieldRef',
				'ReturnsExpirationDateFieldRef',
				'ReturnsDateReturnedFieldRef',
				'ReturnsNewShippingDateFieldRef',
				'ReturnsSubmitButtonRef',
				'ReturnsActionColumnRef',
				'ReturnsFilterDropdownRef',
				'requestsTableRef',
				'addButtonRequestsTableRef',
				'singleOrderButtonRef',
				'BulkOrderButtonRef',
				'exportButtonRequestsTableRef',
				'OrdersActionHamburgerRef',
			];
			const shouldOpenInvoices = (ref: string) =>
				invoicesSubmenuRefs.includes(ref) || invoicesPageRefs.includes(ref);
			const shouldOpenOrders = (ref: string) =>
				ordersSubmenuRefs.includes(ref) || ordersPageRefs.includes(ref);

			// In mobile/tablet mode, open drawer and expand items
			if (isSidebarMode && openDrawer && setDrawerExpandedItems) {
				openDrawer();
				
				// Wait a bit for drawer to open, then expand items
				setTimeout(() => {
					const itemsToExpand: string[] = [];
					
					if (stepReference) {
						if (stepReference === 'InvoicesSalesRepNavItemRef') {
							invoicesMenuExplicitlyClosedRef.current = false;
							itemsToExpand.push('Invoices');
						} else if (shouldOpenInvoices(stepReference)) {
							invoicesMenuExplicitlyClosedRef.current = false;
							
							// Use Accounting & Finance if user has permission (for both onboarding and individual tutorials)
							// Otherwise, use standalone Invoices menu
							if (hasAccountingFinancePermission) {
								itemsToExpand.push('Accounting & Finance');
								itemsToExpand.push('Accounting & Finance-Invoices');
							} else {
								itemsToExpand.push('Invoices');
							}
						} else if (shouldOpenOrders(stepReference)) {
							ordersMenuExplicitlyClosedRef.current = false;
							itemsToExpand.push('Orders');
						}
					} else {
						// Fallback logic
						if (isAnyInvoiceTutorialActive) {
							invoicesMenuExplicitlyClosedRef.current = false;
							itemsToExpand.push('Invoices');
						}
						if (isAnyOrdersTutorialActive) {
							ordersMenuExplicitlyClosedRef.current = false;
							itemsToExpand.push('Orders');
						}
					}
					
					if (itemsToExpand.length > 0) {
						setDrawerExpandedItems(itemsToExpand);
					}
				}, 300);
				return;
			}

			// Desktop mode - use hover menu
			// If step reference is provided, use it to determine which menu to open
			if (stepReference) {
				if (stepReference === 'InvoicesSalesRepNavItemRef') {
					openMenuByType('salesInvoices');
					return;
				}
				if (shouldOpenInvoices(stepReference)) {
					openMenuByType('invoices');
					return;
				}
				
				if (shouldOpenOrders(stepReference)) {
					openMenuByType('orders');
					return;
				}
			}

			// Fallback to original logic if no step reference is provided
			if (isAnyInvoiceTutorialActive) {
				openMenuByType('invoices');
			}
			if (isAnyOrdersTutorialActive) {
				openMenuByType('orders');
			}
		});

		return () => {
			tutorialActions.unregister('closeTopBarMenu');
			tutorialActions.unregister('openInvoicesTopBarMenu');
			tutorialActions.unregister('openInvoiceTobBarMenu');
			tutorialActions.unregister('openOrderTopBarMenu');
			tutorialActions.unregister('openReturnTopBarMenu');
			tutorialActions.unregister('openSalesInvoicesTopBarMenu');
			tutorialActions.unregister('openTobBarMenu');
		};
	}, [
		isTourActive,
		cancelClose,
		setOpenMenu,
		isAnyInvoiceTutorialActive,
		isAnyOrdersTutorialActive,
		isSidebarMode,
		openDrawer,
		setDrawerExpandedItems,
		isOnboardingActive,
		hasAccountingFinancePermission,
	]);

	return {
		isInvoicesTutorialActive,
		isInvoicesSalesRepTutorialActive,
		isReturnsTutorialActive,
		isRequestsTutorialActive,
		tourExpandSubmenu,
		openInvoicesNavItemRef,
		returnsNavItemRef,
		openInvoicesElement,
		returnsElement,
		ordersElement,
		handleSubmenuItemRef,
		setOrdersNavItemRef,
		setAccountingFinanceNavItemRef,
		setInvoicesNavItemRef,
		getTourElements: () => ({
			BVBenefitVerificationNavItemRef: bvNavItemRef.current,
			ordersNavItemRef: ordersElement || ordersNavItemRef.current,
			accountingFinanceNavItemRef: hasAccountingFinancePermission
				? accountingFinanceElement || accountingFinanceNavItemRef.current
				: null,
			invoicesNavItemRef: invoicesElement || invoicesNavItemRef.current,
			InvoicesNavItemRef: openInvoicesElement || openInvoicesNavItemRef.current,
			InvoicesSalesRepNavItemRef:
				openInvoicesSalesRepElement || openInvoicesSalesRepNavItemRef.current,
			ReturnsNavItemRef: returnsElement || returnsNavItemRef.current,
			OrdersNavItemRef: orderStatusElement || orderStatusNavItemRef.current,
		}),
	};
};
